/**
 * src/hooks/usePaymentStatus.ts
 *
 * Fetches the caller's PaymentMethodSummary from /StripePaymentMethod.
 * Re-fetches on tab visibility change so a card-add in another tab
 * (Stripe Customer Portal) is reflected when the user comes back.
 *
 * Used by:
 *   - PaymentStatusBanner (dashboard past-due / suspended banner)
 *   - useOnboardingStatus (gated welcome-flow completion check)
 *   - Dashboard order-CTA gating (disable Order Crate until card on file)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getPaymentMethod, type PaymentMethodSummary } from '../api/billingApi';

export interface UsePaymentStatusResult {
  pm: PaymentMethodSummary | null;
  isLoading: boolean;
  refetch: () => void;
}

export function usePaymentStatus(): UsePaymentStatusResult {
  const [pm, setPm] = useState<PaymentMethodSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const unmountedRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    try {
      const summary = await getPaymentMethod();
      if (!unmountedRef.current) {
        setPm(summary);
        setIsLoading(false);
      }
    } catch {
      // Decorative — silent on error. Banner / checklist self-hide when
      // pm is null, which is the safe fallback.
      if (!unmountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    void fetchOnce();
    function onVisible() {
      if (document.visibilityState === 'visible') void fetchOnce();
    }
    function onPageShow() { void fetchOnce(); }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      unmountedRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [fetchOnce]);

  return { pm, isLoading, refetch: fetchOnce };
}
